export const VISUAL_ACCEPTANCE_QUERY_KEY = "visual-acceptance";
export const IMMERSIVE_ACCEPTANCE_QUERY_KEY = "immersive-acceptance";

export interface VisualAcceptanceDiagnosticMessage {
  readonly level: "warning" | "error" | "uncaught" | "unhandled-rejection";
  readonly message: string;
  readonly occurredAt: string;
}

const diagnosticMessages: VisualAcceptanceDiagnosticMessage[] = [];
let diagnosticsInstalled = false;

function summarizeArgument(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === "string") return value;
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return Object.prototype.toString.call(value);
    }
  }
  return String(value);
}

function recordDiagnostic(
  level: VisualAcceptanceDiagnosticMessage["level"],
  values: readonly unknown[],
) {
  diagnosticMessages.push({
    level,
    message: values.map(summarizeArgument).join(" "),
    occurredAt: new Date().toISOString(),
  });
}

export function isVisualAcceptanceRequest(search = window.location.search): boolean {
  return new URLSearchParams(search).get(VISUAL_ACCEPTANCE_QUERY_KEY) === "1";
}

export function isImmersiveAcceptanceRequest(search = window.location.search): boolean {
  return new URLSearchParams(search).get(IMMERSIVE_ACCEPTANCE_QUERY_KEY) === "1";
}

export function installVisualAcceptanceDiagnostics(): void {
  if (diagnosticsInstalled || (!isVisualAcceptanceRequest() && !isImmersiveAcceptanceRequest()))
    return;
  diagnosticsInstalled = true;

  const originalWarn = console.warn.bind(console);
  const originalError = console.error.bind(console);
  console.warn = (...values: unknown[]) => {
    recordDiagnostic("warning", values);
    originalWarn(...values);
  };
  console.error = (...values: unknown[]) => {
    recordDiagnostic("error", values);
    originalError(...values);
  };

  window.addEventListener("error", (event) => {
    recordDiagnostic("uncaught", [event.message || event.error || "window error"]);
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordDiagnostic("unhandled-rejection", [event.reason]);
  });
}

export function readVisualAcceptanceDiagnostics(): readonly VisualAcceptanceDiagnosticMessage[] {
  return [...diagnosticMessages];
}
